import dayjs from "dayjs";
import {
  TRANSACTION_DATE_FORMAT,
  TRANSACTION_TIME_FORMAT,
} from "@zenith/data/constants";
import { TransactionBaseDTO } from "@zenith/services/user";
import "./ExtensionMethods";

export function formatTransactionDate(date: string | Date | undefined): string {
  if (!date) return "";
  return dayjs(date).format(TRANSACTION_DATE_FORMAT);
}

export function formatTransactionTime(date: string | Date | undefined): string {
  if (!date) return "";
  return dayjs(date).format(TRANSACTION_TIME_FORMAT);
}

export function shouldShow(value: any): boolean {
  return value !== undefined && value !== null && value !== "";
}

const DESCRIPTION_MAX_LENGTH = 80;

export function truncateText(
  text: string | undefined,
  maxLength: number = DESCRIPTION_MAX_LENGTH
): string {
  if (!text) return "";
  return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
}

export function getPaymentMethodDisplay(
  paymentMethod: string | undefined
): string {
  if (paymentMethod) {
    return paymentMethod.mapPaymentMethod();
  }
  return "Unknown";
}

export function isPaymentLinkCard(paymentMethod: string | undefined): boolean {
  return paymentMethod === "PAYMENT_LINK_CARD";
}

export function isPaymentLinkUSSD(paymentMethod: string | undefined): boolean {
  return paymentMethod === "PAYMENT_LINK_USSD";
}

export function isPaymentLinkQRCode(
  paymentMethod: string | undefined
): boolean {
  return paymentMethod === "PAYMENT_LINK_QR_CODE";
}

export function isPaymentLinkPayWithTransfer(
  paymentMethod: string | undefined
): boolean {
  return paymentMethod === "PAYMENT_LINK_PAY_WITH_TRANSFER";
}

export function isPaymentLink(paymentMethod: string | undefined): boolean {
  return paymentMethod?.startsWith("PAYMENT_LINK") || false;
}

export function isCashPayment(paymentMethod: string | undefined): boolean {
  return paymentMethod === "CASH";
}

export function isUSSD(paymentMethod: string | undefined): boolean {
  return paymentMethod === "USSD";
}

export function isQRCode(paymentMethod: string | undefined): boolean {
  return paymentMethod === "QR_CODE";
}

export function getCardType(maskedPan: string | undefined): string {
  if (!maskedPan) return "Card";
  return maskedPan.getCardType();
}

const PAYMENT_METHOD_BUCKET_LABELS: Record<string, string> = {
  CARD: "Card",
  TRANSFER: "Bank Transfer",
  USSD: "USSD Code",
  QR_CODE: "QR Code",
  PAYMENT_LINK: "Payment Link",
  CASH: "Cash",
};

/**
 * The order the design lists the reporting buckets in — Bank Transfer, Card,
 * Payment Link, USSD Code, QR Code. The summary endpoints return them in an
 * arbitrary order, so screens sort against this.
 */
export const PAYMENT_METHOD_BUCKET_ORDER = [
  "TRANSFER",
  "CARD",
  "PAYMENT_LINK",
  "USSD",
  "QR_CODE",
] as const;

/**
 * The outlet and POS summary endpoints report payment methods as reporting
 * buckets (CARD, TRANSFER, USSD, QR_CODE, PAYMENT_LINK) rather than platform
 * enums, so they cannot go through mapPaymentMethod. Anything unmapped falls
 * back to a readable form so a newly added bucket still renders.
 */
export function getPaymentMethodBucketLabel(
  paymentMethod: string | undefined
): string {
  if (!paymentMethod) return "Unknown";
  return (
    PAYMENT_METHOD_BUCKET_LABELS[paymentMethod] ??
    paymentMethod
      .toLowerCase()
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ")
  );
}

export function getPaymentMethodFromOperation(data: any): string {
  if (data?.paymentMethod) {
    return data.paymentMethod;
  }
  if (data?.paymentLink?.paymentLinkMethod) {
    const method = data.paymentLink.paymentLinkMethod;
    switch (method) {
      case "CARD":
        return "PAYMENT_LINK_CARD";
      case "USSD":
        return "PAYMENT_LINK_USSD";
      case "QR_CODE":
        return "PAYMENT_LINK_QR_CODE";
      case "IN_BANK_TRANSFER":
      case "PAY_WITH_TRANSFER":
        return "PAYMENT_LINK_PAY_WITH_TRANSFER";
      default:
        return `PAYMENT_LINK_${method}`;
    }
  }

  return data?.paymentMethod;
}

/**
 * Money leaving the merchant account. A terminal's list reports the direction
 * through the payment method rather than a field of its own, and everything else
 * it can report there is a collection, so an unfamiliar method counts as money in.
 */
const OUTBOUND_PAYMENT_METHODS = [
  "OUT_BANK_TRANSFER",
  "REFUND",
  "TRANSACTION_CHARGE",
  "STAMP_DUTY",
];

/**
 * A terminal's transaction list answers with a slimmer record than the generated
 * `TransactionBaseDTO` — `{ id, amount, paymentMethod, reference, createdAt }` —
 * so the POS screens read their rows through this. The documented field names are
 * accepted too, which keeps those screens right whichever shape comes back.
 */
export interface PosTransactionRecord {
  id?: string;
  amount?: number;
  paymentMethod?: string;
  reference?: string;
  createdAt?: string;
  transactionId?: string;
  totalAmount?: number;
  transactionType?: string;
  description?: string;
  currentAccountBalance?: number;
}

/** A terminal transaction, as the POS screens render it. */
export interface PosTransactionRow {
  id: string;
  description?: string;
  paymentMethod?: string;
  createdAt?: string;
  amount?: number;
  balance?: number;
  isIn: boolean;
}

export function toPosTransactionRow(
  record: PosTransactionRecord
): PosTransactionRow {
  return {
    id: record.id ?? record.transactionId ?? record.reference ?? "",
    description: record.description,
    paymentMethod: record.paymentMethod,
    createdAt: record.createdAt,
    amount: record.amount ?? record.totalAmount,
    balance: record.currentAccountBalance,
    // The documented DTO states the direction; the live payload only implies it.
    isIn: record.transactionType
      ? record.transactionType === TransactionBaseDTO.transactionType.IN
      : !OUTBOUND_PAYMENT_METHODS.includes(record.paymentMethod ?? ""),
  };
}
